import { useState, useEffect } from 'react';
import { ServiceCategory } from './BookingFlow';
import { AlertCircle } from 'lucide-react';
import { apiClient } from '../../../../api/axios';
import { listBusinessServices, type BusinessService } from '../../../../api/professionals';

interface StaffMember {
  id: string;
  displayName: string | null;
  title: string | null;
  specializations: string[] | null;
}

interface ServiceDetailsProps {
  category: ServiceCategory;
  professional: any;
  serviceDetails: any;
  onDetailsChange: (details: any) => void;
}

export function ServiceDetails({ category, professional, serviceDetails, onDetailsChange }: ServiceDetailsProps) {
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [apiServices, setApiServices] = useState<BusinessService[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);

  // Fetch service staff for the selected professional
  useEffect(() => {
    if (!professional?.id) return;
    let cancelled = false;
    apiClient.get<{ staff: StaffMember[] }>(`/professionals/${professional.id}/staff`)
      .then(({ data }) => { if (!cancelled) setStaff(data.staff ?? []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [professional?.id]);

  // Fetch the business's active services for the dynamic Visit Reason dropdown
  useEffect(() => {
    if (!professional?.id) return;
    let cancelled = false;
    setLoadingServices(true);
    listBusinessServices(professional.id)
      .then((services) => {
        if (!cancelled) setApiServices(services);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingServices(false);
      });
    return () => {
      cancelled = true;
    };
  }, [professional?.id]);

  const dynamicServices = apiServices.filter((s) => s.isActive);
  const useDynamicServices = !!professional?.id && dynamicServices.length > 0;

  const updateDetail = (key: string, value: any) => {
    onDetailsChange({ ...serviceDetails, [key]: value });
  };

  const pickDynamicService = (serviceId: string) => {
    const svc = dynamicServices.find((s) => s.id === serviceId);
    onDetailsChange({
      ...serviceDetails,
      reason: svc?.name ?? '',
      serviceId: svc?.id ?? null,
      serviceName: svc?.name ?? null,
      serviceType: svc?.category ?? null,
      serviceCategory: svc?.category ?? null,
      estimatedPrice: svc?.price ?? null,
    });
  };

  // Veterinary-specific form (was 'veterinarian', now 'veterinary')
  if (category === 'veterinary') {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-lg p-4 flex items-start gap-3">
          <div className="text-2xl">🏥</div>
          <div>
            <h3 className="mb-2 dark:text-white">Veterinary Appointment Details</h3>
            <p className="text-sm text-gray-700 dark:text-white/70">Medical-focused booking for professional care</p>
          </div>
        </div>

        <div>
          <label className="block text-sm mb-2 dark:text-white/70">Visit Reason *</label>
          {loadingServices && professional?.id ? (
            <div className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] rounded-lg flex items-center gap-2 text-gray-500 dark:text-white/50 text-sm">
              <div className="w-4 h-4 border-2 border-gray-300 dark:border-white/30 border-t-blue-600 rounded-full animate-spin" />
              Loading services...
            </div>
          ) : useDynamicServices ? (
            <select
              value={serviceDetails.serviceId || ''}
              onChange={(e) => pickDynamicService(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] dark:text-white rounded-lg"
            >
              <option value="">Select a service</option>
              {dynamicServices.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.price ? ` — ${s.currency || ''} ${s.price}` : ''}
                </option>
              ))}
            </select>
          ) : (
            <select
              value={serviceDetails.reason || ''}
              onChange={(e) => updateDetail('reason', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] dark:text-white rounded-lg"
            >
              <option value="">Select reason for visit</option>
              <option value="vaccination">Vaccination</option>
              <option value="checkup">General Check-up</option>
              <option value="emergency">Emergency</option>
              <option value="followup">Follow-up Visit</option>
              <option value="dental">Dental Care</option>
              <option value="surgery">Surgical Consultation</option>
              <option value="other">Other</option>
            </select>
          )}
          {!loadingServices && !useDynamicServices && professional?.id && (
            <p className="mt-1 text-xs text-gray-500 dark:text-white/50">
              This business hasn't listed services yet — pick a general reason.
            </p>
          )}
        </div>

        {serviceDetails.reason === 'emergency' && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800/30 rounded-lg p-4">
            <div className="flex items-center gap-2 text-red-800 dark:text-red-300 mb-2">
              <AlertCircle className="w-5 h-5" />
              <strong>Emergency Priority</strong>
            </div>
            <p className="text-sm text-red-700 dark:text-red-300">
              This will flag your appointment as urgent. The clinic will contact you immediately.
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm mb-2 dark:text-white/70">Symptoms / Notes (Optional)</label>
          <textarea
            value={serviceDetails.notes || ''}
            onChange={(e) => updateDetail('notes', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] dark:text-white rounded-lg resize-none"
            rows={4}
            placeholder="Describe any symptoms, concerns, or specific requests..."
          />
        </div>

        <div>
          <label className="block text-sm mb-2 dark:text-white/70">Preferred Veterinarian (Optional)</label>
          <select
            value={serviceDetails.preferredVet || ''}
            onChange={(e) => updateDetail('preferredVet', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] dark:text-white rounded-lg"
          >
            <option value="">No preference</option>
            {staff.map((s) => (
              <option key={s.id} value={s.id}>
                {s.displayName || 'Staff member'}{s.title ? ` — ${s.title}` : ''}
              </option>
            ))}
            {staff.length === 0 && <option value="" disabled>No staff listed</option>}
          </select>
        </div>
      </div>
    );
  }

  // Grooming-specific form
  if (category === 'grooming') {
    return (
      <div className="space-y-6">
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800/30 rounded-lg p-4 flex items-start gap-3">
          <div className="text-2xl">✂️</div>
          <div>
            <h3 className="mb-2 dark:text-white">Grooming Service Details</h3>
            <p className="text-sm text-gray-700 dark:text-white/70">Visual, service-driven grooming options</p>
          </div>
        </div>

        <div>
          <label className="block text-sm mb-2 dark:text-white/70">Service Type *</label>
          <div className="grid md:grid-cols-2 gap-3">
            {['Full Groom', 'Bath & Brush', 'Nail Trim Only', 'Haircut Only'].map(service => (
              <label
                key={service}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  serviceDetails.serviceType === service
                    ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/20 dark:border-purple-500'
                    : 'border-gray-300 dark:border-white/20 hover:border-gray-400 dark:hover:border-white/30'
                }`}
              >
                <input
                  type="radio"
                  name="serviceType"
                  value={service}
                  checked={serviceDetails.serviceType === service}
                  onChange={(e) => updateDetail('serviceType', e.target.value)}
                  className="sr-only"
                />
                <div className="dark:text-white">{service}</div>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm mb-2 dark:text-white/70">Coat Type</label>
          <select
            value={serviceDetails.coatType || ''}
            onChange={(e) => updateDetail('coatType', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] dark:text-white rounded-lg"
          >
            <option value="">Select coat type (auto-suggested)</option>
            <option value="short">Short Hair</option>
            <option value="medium">Medium Hair</option>
            <option value="long">Long Hair</option>
            <option value="curly">Curly/Wavy</option>
            <option value="double">Double Coat</option>
          </select>
        </div>

        <div>
          <label className="block text-sm mb-2 dark:text-white/70">Behavior Notes (Optional)</label>
          <textarea
            value={serviceDetails.behaviorNotes || ''}
            onChange={(e) => updateDetail('behaviorNotes', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] dark:text-white rounded-lg resize-none"
            rows={3}
            placeholder="e.g., Anxious with nail trimming, loves water, sensitive ears..."
          />
        </div>

        <div>
          <label className="block text-sm mb-2 dark:text-white/70">Special Requests</label>
          <textarea
            value={serviceDetails.specialRequests || ''}
            onChange={(e) => updateDetail('specialRequests', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] dark:text-white rounded-lg resize-none"
            rows={2}
            placeholder="Specific styling, products to avoid, etc."
          />
        </div>
      </div>
    );
  }

  // Walking-specific form (was 'walker', now 'walking')
  if (category === 'walking') {
    return (
      <div className="space-y-6">
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 rounded-lg p-4 flex items-start gap-3">
          <div className="text-2xl">🚶</div>
          <div>
            <h3 className="mb-2 dark:text-white">Pet Walking Service Details</h3>
            <p className="text-sm text-gray-700 dark:text-white/70">Schedule-based, recurring-friendly options</p>
          </div>
        </div>

        <div>
          <label className="block text-sm mb-2 dark:text-white/70">Walk Duration *</label>
          <div className="grid grid-cols-3 gap-3">
            {['30 minutes', '45 minutes', '60 minutes'].map(duration => (
              <label
                key={duration}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all text-center ${
                  serviceDetails.duration === duration
                    ? 'border-green-600 bg-green-50 dark:bg-green-900/20 dark:border-green-500'
                    : 'border-gray-300 dark:border-white/20 hover:border-gray-400 dark:hover:border-white/30'
                }`}
              >
                <input
                  type="radio"
                  name="duration"
                  value={duration}
                  checked={serviceDetails.duration === duration}
                  onChange={(e) => updateDetail('duration', e.target.value)}
                  className="sr-only"
                />
                <div className="dark:text-white">{duration}</div>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm mb-2 dark:text-white/70">Frequency *</label>
          <select
            value={serviceDetails.frequency || ''}
            onChange={(e) => updateDetail('frequency', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] dark:text-white rounded-lg"
          >
            <option value="">Select frequency</option>
            <option value="one-time">One-time Walk</option>
            <option value="daily">Daily</option>
            <option value="weekdays">Weekdays Only</option>
            <option value="weekends">Weekends Only</option>
            <option value="custom">Custom Schedule</option>
          </select>
        </div>

        <div>
          <label className="block text-sm mb-2 dark:text-white/70">Access Notes</label>
          <textarea
            value={serviceDetails.accessNotes || ''}
            onChange={(e) => updateDetail('accessNotes', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] dark:text-white rounded-lg resize-none"
            rows={3}
            placeholder="Gate codes, key location, building access instructions..."
          />
        </div>

        <div>
          <label className="block text-sm mb-2 dark:text-white/70">Walking Preferences</label>
          <textarea
            value={serviceDetails.preferences || ''}
            onChange={(e) => updateDetail('preferences', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] dark:text-white rounded-lg resize-none"
            rows={2}
            placeholder="Preferred routes, areas to avoid, leash behavior, etc."
          />
        </div>
      </div>
    );
  }

  // Boarding-specific form
  if (category === 'boarding') {
    return (
      <div className="space-y-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg p-4 flex items-start gap-3">
          <div className="text-2xl">🏠</div>
          <div>
            <h3 className="mb-2 dark:text-white">Boarding / Hotel Details</h3>
            <p className="text-sm text-gray-700 dark:text-white/70">Multi-day stay information</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm mb-2 dark:text-white/70">Check-in Date *</label>
            <input
              type="date"
              value={serviceDetails.checkin || ''}
              onChange={(e) => updateDetail('checkin', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] dark:text-white rounded-lg"
              min={new Date().toISOString().split('T')[0]}
            />
          </div>
          <div>
            <label className="block text-sm mb-2 dark:text-white/70">Check-out Date *</label>
            <input
              type="date"
              value={serviceDetails.checkout || ''}
              onChange={(e) => updateDetail('checkout', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] dark:text-white rounded-lg"
              min={serviceDetails.checkin || new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm mb-2 dark:text-white/70">Feeding Schedule *</label>
          <textarea
            value={serviceDetails.feedingSchedule || ''}
            onChange={(e) => updateDetail('feedingSchedule', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] dark:text-white rounded-lg resize-none"
            rows={3}
            placeholder="e.g., 1 cup dry food morning and evening, water always available..."
          />
        </div>

        <div>
          <label className="block text-sm mb-2 dark:text-white/70">Special Care Needs</label>
          <textarea
            value={serviceDetails.specialCare || ''}
            onChange={(e) => updateDetail('specialCare', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] dark:text-white rounded-lg resize-none"
            rows={3}
            placeholder="Medications, medical conditions, behavioral considerations..."
          />
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/30 rounded-lg p-4">
          <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-300 mb-2">
            <AlertCircle className="w-5 h-5" />
            <strong>Vaccination Proof Required</strong>
          </div>
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            Boarding facilities require up-to-date vaccination records. We'll share your pet's vaccination status automatically.
          </p>
        </div>
      </div>
    );
  }

  // Generic form for all other categories
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg p-4 flex items-start gap-3">
        <div className="text-2xl">📝</div>
        <div>
          <h3 className="mb-2 dark:text-white">Service Details for {professional?.name}</h3>
          <p className="text-sm text-gray-700 dark:text-white/70">Provide details about your booking</p>
        </div>
      </div>

      <div>
        <label className="block text-sm mb-2 dark:text-white/70">Service Type *</label>
        <select
          value={serviceDetails.serviceType || ''}
          onChange={(e) => updateDetail('serviceType', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] dark:text-white rounded-lg"
        >
          <option value="">Select service type</option>
          {professional?.services?.map((service: string, index: number) => (
            <option key={index} value={service}>{service}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm mb-2 dark:text-white/70">Duration *</label>
        <select
          value={serviceDetails.duration || ''}
          onChange={(e) => updateDetail('duration', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] dark:text-white rounded-lg"
        >
          <option value="">Select duration</option>
          <option value="30">30 minutes</option>
          <option value="60">1 hour</option>
          <option value="90">1.5 hours</option>
          <option value="120">2 hours</option>
        </select>
      </div>

      <div>
        <label className="block text-sm mb-2 dark:text-white/70">Special Requests / Notes</label>
        <textarea
          value={serviceDetails.notes || ''}
          onChange={(e) => updateDetail('notes', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 dark:bg-[#0F0E16] dark:text-white rounded-lg resize-none"
          rows={4}
          placeholder="Any specific requirements, preferences, or important information..."
        />
      </div>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 rounded-lg p-4">
        <p className="text-sm text-blue-700 dark:text-blue-300">
          ℹ️ Your pet's health records and relevant information will be shared with the professional based on your preferences in the next step.
        </p>
      </div>
    </div>
  );
}